import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Bar, BarChart, CartesianGrid, Cell, Pie, PieChart, ReferenceArea, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { categorySpend, incomeVsSpend, spendProjection, type BudgetLine, type Expense, type ExpenseCategory, type Income } from "@torre/expenses";
import { useTheme } from "../../../context/ThemeContext";
import { CATEGORY_COLORS, categoryLabel, lang, money, monthLabel } from "../lib/format";
import { ui } from "../theme";

// Gráficas del registro de gastos, sobre los gastos que ya están filtrados en
// pantalla (en MXN): en qué categorías se fue el dinero del periodo, cómo se
// repartió mes a mes y cuánto es gasto fijo y cuánto variable. Reciben los
// gastos SIN el filtro de categoría: la categoría elegida se resalta y las
// demás se atenúan, para poder compararla y elegir otra sin quitar el filtro.
// «Gasto mes a mes» agrega la proyección de los 3 meses que vienen (presupuesto
// del mes por categoría; sin presupuesto, el promedio de los meses cerrados) y
// una vista de ingresos contra egresos.

/** Fijo y variable, validados en claro y oscuro. */
export const COST_TYPE_COLORS = { fijo: "#0d9488", variable: "#d97706" } as const;
/** Ingresos y egresos. */
export const CASHFLOW_COLORS = { income: "#059669", spend: "#dc2626" } as const;
/** Opacidad de las barras proyectadas: se ven, pero no se confunden con lo ya gastado. */
const PROJECTED_OPACITY = 0.45;

type View = "category" | "costType" | "cashflow";
const VIEWS: readonly View[] = ["category", "costType", "cashflow"];

/** Marca del eje: `$1.5k`, `$12k`, `-$500` (un decimal bajo 10k para que dos marcas no se lean igual). */
export const kFormat = (v: unknown) => {
  const n = Number(v);
  const abs = Math.abs(n);
  const sign = n < 0 ? "-" : "";
  if (abs < 1000) return `${sign}$${Math.round(abs)}`;
  const k = abs / 1000;
  return `${sign}$${k < 10 ? Number(k.toFixed(1)) : Math.round(k)}k`;
};

const percent = (share: number, language: string | undefined) =>
  new Intl.NumberFormat(lang(language) === "en" ? "en-US" : "es-MX", { style: "percent", maximumFractionDigits: 1 }).format(share);

type Row = Pick<Expense, "date" | "category" | "amountBase" | "costType"> & { personal?: boolean };

export default function ExpenseCategoryCharts({
  rows,
  category = "",
  onSelectCategory,
  budgets,
  incomes,
  today,
  costType = "",
}: {
  rows: readonly Row[];
  /** Gastos de todas las categorías (el filtro de categoría se aplica aparte, en la lista). */
  /** Categoría filtrada en la lista ("" = todas): se resalta y las demás se atenúan. */
  category?: string;
  /** Clic en una categoría de la leyenda: la filtra, o quita el filtro si ya lo era. */
  onSelectCategory?: (category: string) => void;
  /**
   * Presupuestos de la empresa. Con ellos (y `today`) la gráfica mensual proyecta
   * los 3 meses que vienen; sin ellos (`undefined`) no hay proyección: es lo que
   * manda la lista cuando tiene filtros que un presupuesto no puede seguir.
   */
  budgets?: readonly Pick<BudgetLine, "period" | "category" | "amount">[];
  /** Ingresos del periodo, para la vista «Ingresos vs egresos». */
  incomes?: readonly (Pick<Income, "period" | "amountBase"> & { personal?: boolean; payerType?: Income["payerType"] })[];
  /** Hoy (`YYYY-MM-DD`): la proyección empieza el mes siguiente. */
  today?: string;
  /** Tipo de costo filtrado en la lista ("" = todos): la proyección se limita a ese tipo. */
  costType?: string;
}) {
  const { t, i18n } = useTranslation("common");
  const language = i18n.language;
  const { isDarkMode } = useTheme();
  const c = ui(isDarkMode);
  const [view, setView] = useState<View>("category");

  // #1178: todas las gráficas miden el gasto de la EMPRESA (sin lo no atribuible),
  // la misma base que Resumen y Presupuestos; así sus cifras y proyecciones cuadran.
  const spend = useMemo(() => categorySpend(rows.filter((r) => r.personal !== true)), [rows]);
  // El pastel no puede dibujar una rebanada negativa (categoría con más devoluciones que gasto).
  const slices = useMemo(() => spend.categories.filter((s) => s.total > 0), [spend]);
  const projection = useMemo(
    () => (budgets && today ? spendProjection({ months: spend.months, budgets, today, costType: costType === "fijo" || costType === "variable" ? costType : null }) : []),
    [spend, budgets, today, costType],
  );
  const monthly = useMemo(
    () =>
      [...spend.months.map((m) => ({ ...m, projected: false })), ...projection.map((m) => ({ ...m, projected: true }))].map((m) => ({
        period: m.period,
        label: monthLabel(m.period, language),
        projected: m.projected,
        fijo: m.fixed,
        variable: m.variable,
        ...m.byCategory,
      })),
    [spend, projection, language],
  );
  // «Ingresos vs egresos» sigue las reglas de Flujo: solo gasto de la empresa (sin lo
  // no atribuible), con su propia proyección, y las devoluciones restan del gasto.
  const companySpend = spend;
  const companyProjection = projection;
  const flow = useMemo(
    () => incomeVsSpend({ months: companySpend.months, incomes: incomes || [], projection: companyProjection, today: today || "" }),
    [companySpend, incomes, companyProjection, today],
  );
  const cashflow = useMemo(
    () =>
      flow.months.map((m) => ({
        period: m.period,
        label: monthLabel(m.period, language),
        projected: m.projected,
        income: m.income,
        spend: m.spend,
      })),
    [flow, language],
  );
  // Categorías que aparecen en la gráfica mensual: las gastadas y las que solo traen proyección.
  const monthlyCategories = useMemo(() => {
    const seen = new Set<ExpenseCategory>(spend.categories.map((s) => s.category));
    for (const m of projection) for (const k of Object.keys(m.byCategory) as ExpenseCategory[]) seen.add(k);
    return [...seen];
  }, [spend, projection]);

  const axis = { fontSize: 11, fill: isDarkMode ? "#9ca3af" : "#6b7280" };
  const grid = isDarkMode ? "#333" : "#e5e7eb";
  const tooltipStyle = { fontSize: 12, backgroundColor: isDarkMode ? "#1f1f1f" : "#fff", borderColor: grid, color: isDarkMode ? "#fff" : "#111" };
  const fmt = (v: unknown) => money(Number(v), "MXN", language);
  const costLabel = (k: "fijo" | "variable") => t(`expenses.costType.${k}`);
  const seriesName = (key: string) =>
    key === "fijo" || key === "variable" ? costLabel(key) : key === "income" || key === "spend" ? t(`expenses.charts.${key}`) : categoryLabel(key, language);
  const data = view === "cashflow" ? cashflow : monthly;
  const projectedLabels = data.filter((d) => d.projected).map((d) => d.label);
  // Totales proyectados que se leen bajo la gráfica: los de la vista en pantalla.
  const shownProjection = view === "cashflow" ? companyProjection : projection;
  const colorOf = (key: string) =>
    key === "fijo" || key === "variable" ? COST_TYPE_COLORS[key] : key === "income" || key === "spend" ? CASHFLOW_COLORS[key] : CATEGORY_COLORS[key as ExpenseCategory];
  const series: string[] = view === "category" ? monthlyCategories : view === "costType" ? ["fijo", "variable"] : ["income", "spend"];
  const fixedShare = spend.fixedShare;
  const dim = (key: string) => (!category || category === key ? 1 : 0.3);

  if (spend.count === 0) return null;

  return (
    <section aria-label={t("expenses.charts.title")} className="grid grid-cols-1 gap-3" data-testid="expense-category-charts">
      <div className={c.card}>
        <h2 className={`text-sm font-semibold ${c.text}`}>{t("expenses.charts.byCategory")}</h2>
        <p className={`mb-2 text-xs ${c.muted}`}>{t("expenses.charts.byCategoryHint")}</p>
        {/* Las dos gráficas van SIEMPRE una debajo de la otra, a todo el ancho; dentro de esta, el pastel va junto a su tabla. */}
        <div className="grid grid-cols-1 items-center gap-3 md:grid-cols-2">
          <div className="h-56">
            {slices.length === 0 ? (
              <p className={`py-20 text-center text-sm ${c.muted}`}>{t("expenses.charts.nothingToDraw")}</p>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={slices} dataKey="total" nameKey="category" innerRadius="55%" outerRadius="90%" paddingAngle={1} stroke="none" isAnimationActive={false}>
                    {slices.map((s) => (
                      <Cell key={s.category} fill={CATEGORY_COLORS[s.category]} opacity={dim(s.category)} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v, name) => [fmt(v), categoryLabel(String(name), language)]} contentStyle={tooltipStyle} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
          <table className="w-full self-center text-sm" aria-label={t("expenses.charts.byCategory")}>
            <tbody>
              {spend.categories.map((s) => (
                <tr key={s.category} className={`border-t first:border-t-0 ${c.border} ${c.text}`} data-testid={`category-total-${s.category}`}>
                  <td className="py-1">
                    <button
                      type="button"
                      className={`inline-flex items-center gap-1.5 text-left text-xs ${category === s.category ? "font-semibold" : ""} ${onSelectCategory ? "hover:underline" : "cursor-default"}`}
                      aria-pressed={category === s.category}
                      disabled={!onSelectCategory}
                      onClick={() => onSelectCategory?.(category === s.category ? "" : s.category)}
                    >
                      <span className="h-2.5 w-2.5 shrink-0 rounded-sm" style={{ background: CATEGORY_COLORS[s.category] }} />
                      {categoryLabel(s.category, language)}
                    </button>
                  </td>
                  <td className="whitespace-nowrap py-1 text-right text-xs tabular-nums">{money(s.total, "MXN", language)}</td>
                  <td className={`w-12 whitespace-nowrap py-1 text-right text-xs tabular-nums ${c.muted}`}>{percent(s.share, language)}</td>
                </tr>
              ))}
              <tr className={`border-t ${c.border} ${c.text} font-semibold`} data-testid="category-total-all">
                <td className="py-1 text-xs">{t("expenses.charts.total")}</td>
                <td className="whitespace-nowrap py-1 text-right text-xs tabular-nums">{money(spend.total, "MXN", language)}</td>
                <td />
              </tr>
            </tbody>
          </table>
        </div>
        <div className="mt-3" data-testid="cost-type-split">
          <div className="flex h-2 overflow-hidden rounded-full" style={{ background: COST_TYPE_COLORS.variable }} aria-hidden>
            <div style={{ width: `${Math.min(100, fixedShare * 100)}%`, background: COST_TYPE_COLORS.fijo }} />
          </div>
          <p className={`mt-1 flex flex-wrap justify-between gap-x-3 text-xs ${c.muted}`}>
            <span>
              <span className="mr-1 inline-block h-2 w-2 rounded-sm" style={{ background: COST_TYPE_COLORS.fijo }} />
              {costLabel("fijo")}: <span className={`tabular-nums ${c.text}`}>{money(spend.fixed, "MXN", language)}</span> ({percent(fixedShare, language)})
            </span>
            <span>
              <span className="mr-1 inline-block h-2 w-2 rounded-sm" style={{ background: COST_TYPE_COLORS.variable }} />
              {costLabel("variable")}: <span className={`tabular-nums ${c.text}`}>{money(spend.variable, "MXN", language)}</span>
            </span>
          </p>
        </div>
        {spend.withoutRate > 0 && <p className={`mt-2 text-xs ${c.muted}`}>{t("expenses.charts.withoutRate", { count: spend.withoutRate })}</p>}
      </div>

      <div className={c.card}>
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <h2 className={`text-sm font-semibold ${c.text}`}>{t("expenses.charts.monthly")}</h2>
            <p className={`mb-2 text-xs ${c.muted}`}>{t(`expenses.charts.hint.${view}`)}</p>
          </div>
          <div className={`inline-flex shrink-0 rounded-lg border p-0.5 text-xs ${c.border}`} role="group" aria-label={t("expenses.charts.view.label")}>
            {VIEWS.map((v) => (
              <button
                key={v}
                type="button"
                aria-pressed={view === v}
                onClick={() => setView(v)}
                className={`rounded-md px-2 py-1 ${view === v ? "bg-teal-600 text-white" : c.muted}`}
              >
                {t(`expenses.charts.view.${v}`)}
              </button>
            ))}
          </div>
        </div>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} stackOffset="sign">
              <CartesianGrid strokeDasharray="3 3" stroke={grid} vertical={false} />
              <XAxis dataKey="label" tick={axis} interval="preserveStartEnd" minTickGap={8} />
              <YAxis tick={axis} tickFormatter={kFormat} width={52} />
              <Tooltip
                formatter={(v, name) => [fmt(v), seriesName(String(name))]}
                labelFormatter={(label) => (projectedLabels.includes(String(label)) ? `${label} · ${t("expenses.charts.projection")}` : String(label))}
                contentStyle={tooltipStyle}
                cursor={{ fill: isDarkMode ? "#ffffff10" : "#0000000a" }}
              />
              {projectedLabels.length > 0 && (
                <ReferenceArea
                  x1={projectedLabels[0]}
                  x2={projectedLabels[projectedLabels.length - 1]}
                  fill={isDarkMode ? "#ffffff" : "#0f172a"}
                  fillOpacity={0.06}
                  strokeOpacity={0}
                  label={{ value: t("expenses.charts.projection"), position: "insideTop", fontSize: 10, fill: axis.fill }}
                />
              )}
              {series.map((key) => (
                // Categorías y fijo/variable se apilan; ingresos y egresos van lado a lado.
                <Bar key={key} dataKey={key} stackId={view === "cashflow" ? undefined : "m"} fill={colorOf(key)} maxBarSize={view === "cashflow" ? 18 : 40} isAnimationActive={false}>
                  {data.map((d) => (
                    <Cell key={d.period} fillOpacity={(view === "category" ? dim(key) : 1) * (d.projected ? PROJECTED_OPACITY : 1)} />
                  ))}
                </Bar>
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
        <p className={`mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs ${c.muted}`}>
          {series.map((k) => (
            <span key={k} className="inline-flex items-center gap-1">
              <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: colorOf(k) }} />
              {seriesName(k)}
            </span>
          ))}
        </p>
        {view === "cashflow" && flow.incomeWithoutRate > 0 && (
          <p className={`mt-2 text-xs ${c.muted}`}>{t("expenses.charts.incomeWithoutRate", { count: flow.incomeWithoutRate })}</p>
        )}
        {shownProjection.length > 0 && (
          <p className={`mt-2 text-xs ${c.muted}`} data-testid="projection-note">
            <span className="mr-1 inline-block h-2.5 w-2.5 rounded-sm align-middle" style={{ background: axis.fill, opacity: PROJECTED_OPACITY }} />
            {t("expenses.charts.projectionNote")}{" "}
            {shownProjection.map((m) => (
              <span key={m.period} className="mr-2 whitespace-nowrap" data-testid={`projection-${m.period}`}>
                {monthLabel(m.period, language)}: <span className={`tabular-nums ${c.text}`}>{money(m.total, "MXN", language)}</span>
              </span>
            ))}
          </p>
        )}
      </div>
    </section>
  );
}
